import { OfferCardStatus } from "./offer-card-status";
import { getStatusColor } from "../utils/helpers";
import { useHasPermissions } from "../../../../../public/auth/infrastructure/store/auth-context";
import { useTranslation } from "react-i18next";
import { ClockIcon, Timer } from "../../../../../shared/infrastructure";
import { OfferCardSectionT, OfferT } from "../../../domain";
import React, { useMemo } from "react";

type TopBarProps = {
  left: React.ReactNode;
  middle: React.ReactNode;
  right: React.ReactNode;
  statusColor: string;
  hiddeStatus?: boolean;
};

const TopBar = ({
  middle,
  right,
  statusColor,
  hiddeStatus = false,
}: TopBarProps): React.ReactElement => (
  <React.Fragment>
    <section className="mx-auto grid grid-flow-col items-center justify-start gap-[8px] tracking-tighter">
      {middle !== null && !hiddeStatus && (
        <span className={`h-[10px] w-[10px] rounded-full ${statusColor}`} />
      )}
      <span className="grid grid-flow-col items-center gap-[8px] whitespace-nowrap text-sm font-[500] tracking-tighter">
        {middle}
      </span>
    </section>
    <span className="mx-auto hidden grid-flow-col items-center justify-start gap-[10px] text-sm font-[500] tracking-tighter md:grid">
      {right}
    </span>
  </React.Fragment>
);

const TopBar2 = ({
  left,
  middle,
  right,
  statusColor,
  hiddeStatus = false,
}: TopBarProps): React.ReactElement => (
  <React.Fragment>
    <section className="flex justify-self-start tracking-tighter">
      {left}
    </section>
    <section className="grid grid-flow-col items-center justify-start gap-[8px] tracking-tighter">
      {middle !== null && !hiddeStatus && (
        <span className={`h-[10px] w-[10px] rounded-full ${statusColor}`} />
      )}
      <span className="grid grid-flow-col items-center gap-[8px] whitespace-nowrap text-sm font-[500] tracking-tighter">
        {middle}
      </span>
    </section>
    <span className="hidden grid-flow-col items-center justify-start gap-[10px] text-sm font-[500] tracking-tighter md:grid">
      {right}
    </span>
  </React.Fragment>
);

type QueueHistoryTopBarProps = {
  data: OfferT;
  statusColor: string;
};

const QueueTopBar = ({
  data,
  statusColor,
}: QueueHistoryTopBarProps): React.ReactElement => {
  // const hasPermissions = useHasPermissions("update:status-approvals-queue");
  return (
    <TopBar
      hiddeStatus
      statusColor={statusColor}
      left={null}
      middle={
        <React.Fragment>
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[400] text-[#484848]">
            Offer Count
          </span>
          <span className="ml-2 text-[12px] font-[500] text-[#484848]">
            {data.approvals_offer_count ? data.approvals_offer_count : "- -"}
          </span>
        </React.Fragment>
      }
      right={
        <React.Fragment>
          <ClockIcon fill="#FFBD2E" />
          <Timer
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            date={data.created_at}
          />
        </React.Fragment>
      }
    />
  );
};

const HistoryTopBar = ({
  data,
  statusColor,
}: QueueHistoryTopBarProps): React.ReactElement => {
  const [t] = useTranslation("global");
  const hasPermissions = useHasPermissions("update:status-approvals-queue");

  return (
    <TopBar2
      statusColor={statusColor}
      left={
        <React.Fragment>
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-sm font-[400] text-[#484848]">
            Offer Count
          </span>
          <span className="ml-1 text-[12px] font-[500] text-[#484848]">
            {data.approvals_offer_count ? data.approvals_offer_count : "- -"}
          </span>
        </React.Fragment>
      }
      middle={
        hasPermissions ? (
          <React.Fragment>
            <OfferCardStatus
              section="history"
              data={data}
              statusColor={statusColor}
            />
          </React.Fragment>
        ) : null
      }
      right={
        <React.Fragment>
          <span className="text-[#9E9E9E]">
            {t<string>("context.private.components.offer-card.approver")}:
          </span>
          <span
            className="overflow-hidden text-ellipsis whitespace-nowrap text-[#3C7CF3]"
            title={data.approver_user?.name ?? "- -"}
          >
            {data.approver_user?.name ?? "- -"}
          </span>
        </React.Fragment>
      }
    />
  );
};

type OfferCardTopBarProps = {
  data: OfferT;
  cardType: OfferCardSectionT;
};

const OfferCardTopBar = ({
  data,
  cardType,
}: OfferCardTopBarProps): React.ReactElement => {
  const statusColor = useMemo<string>(
    () => getStatusColor(data.status, "bg"),
    [data.status]
  );

  return (
    <section
      className={["mt-4 flex md:mt-0", "items-center justify-between "].join(
        " "
      )}
    >
      {cardType === "queue" && (
        <QueueTopBar statusColor={statusColor} data={data} />
      )}
      {cardType === "history" && (
        <HistoryTopBar statusColor={statusColor} data={data} />
      )}
    </section>
  );
};

type RepCardTopBarProps = {
  data: OfferT;
};

const RepCardTopBar = ({ data }: RepCardTopBarProps): React.ReactElement => {
  const [t] = useTranslation("global");

  return (
    <section className="flex items-center justify-center md:grid md:h-[34px] md:grid-flow-col md:justify-between xl:h-auto">
      <section className="grid grid-flow-col items-baseline gap-[20px] md:items-center">
        <span className="pt-4 text-sm font-medium capitalize text-[#FF9800] md:pt-0 xl:pt-4 xl:text-[20px]">
          {data.customer?.name ?? "- -"}
        </span>
        <span className="text-xs font-normal uppercase text-[#919191] md:text-sm">
          {data.customer?.customer_id ?? "- -"}
        </span>
      </section>
      <section className="hidden grid-flow-col items-baseline justify-start gap-[16px] md:grid">
        <span className="grid grid-flow-col justify-start gap-[5px] text-sm font-medium text-[#919191] md:hidden xl:grid">
          <span>
            {t<string>(
              "context.private.components.offer-card-map.order-number"
            )}
          </span>
          <span className="text-black">{data.order_number}</span>
        </span>
      </section>
    </section>
  );
};

export { OfferCardTopBar, RepCardTopBar };
